import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const TopHeader = ({
	onSignOut,
	onSettings,
	onDashboard,
	onAdmin,
	isAdmin = false,
	showAdmin = false,
	showDashboard = false,
	showAuthActions = false,
	adminLabelKey = 'adminDashboard',
	currentUser,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [menuOpen, setMenuOpen] = useState(false);
	const { locale, setLocale, t } = useLanguage();
	const onAdminPage = location?.pathname === '/admin';

	const languages = ['en', 'tr', 'fr', 'uk', 'es', 'ar'];
	// Display labels include a country flag emoji for quick recognition
	const langLabels = { en: '🇬🇧 EN', tr: '🇹🇷 TR', fr: '🇫🇷 FR', uk: '🇺🇦 UA', es: '🇪🇸 ES', ar: '🇸🇦 AR' };
	const getNextLocale = (current) => {
		const i = languages.indexOf(current);
		return languages[(i + 1) % languages.length];
	};

	const [langMenuOpen, setLangMenuOpen] = useState(false);

	// Menu items array for easier mapping
	const menuItems = [];

	// Show "My Dashboard" when logged in
	if (showDashboard) {
		menuItems.push({
			labelKey: 'myDashboard',
			defaultLabel: 'My Dashboard',
			onClick: onDashboard,
			className: 'text-green-600 hover:bg-green-100',
		});
	}

	// Show "Admin Dashboard" only if user is admin and not on admin page
	if (showAdmin && isAdmin) {
		menuItems.push({
			labelKey: adminLabelKey,
			defaultLabel: 'Admin Dashboard',
			onClick: onAdmin,
			className: 'text-purple-600 hover:bg-purple-100',
		});
	}

	// Show "My Dashboard" link when on admin page to navigate back
	if (onAdminPage && showAuthActions) {
		menuItems.push({
			labelKey: 'myDashboard',
			defaultLabel: 'My Dashboard',
			onClick: onDashboard,
			className: 'text-green-600 hover:bg-green-100',
		});
	}

	if (showAuthActions) {
		menuItems.push({
			labelKey: 'settings',
			defaultLabel: 'Settings',
			onClick: onSettings,
			className: 'text-blue-600 hover:bg-blue-100',
		});
		menuItems.push({
			labelKey: 'signOut',
			defaultLabel: 'Sign Out',
			onClick: onSignOut,
			className: 'text-red-600 hover:bg-red-100',
		});
	}

	return (
		<header className='w-full fixed top-0 left-0 z-50 bg-white border-b border-gray-200 shadow-sm'>
			<div className='max-w-6xl mx-auto flex items-center justify-between px-4 py-2'>
				{/* Logo - click to go to landing page */}
				<div
					className='flex items-center cursor-pointer hover:opacity-80 transition'
					onClick={() => navigate('/')}
				>
					<img src='/logo2.png' alt='resolvo.me logo' className='h-8 mr-2' />
				</div>
				{/* Responsive Menu */}
				<nav className='relative'>
					{/* Mobile menu button */}
					<button
						type='button'
						className='sm:hidden flex items-center px-2 py-1 border rounded text-gray-700 border-gray-300 hover:bg-gray-100 focus:outline-none'
						aria-label='Open menu'
						aria-expanded={menuOpen}
						onClick={() => setMenuOpen((open) => !open)}
					>
						<svg
							className='h-6 w-6'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M4 6h16M4 12h16M4 18h16'
							/>
						</svg>
					</button>
					{/* Desktop menu */}
					<ul className='hidden sm:flex items-center space-x-2 sm:space-x-4'>
						{menuItems.map((item) => {
							const key = item.labelKey || item.defaultLabel || item.label;
							return (
								<li key={key}>
									<button
										onClick={item.onClick}
										className={`px-3 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${item.className}`}
									>
										{item.labelKey ? t(item.labelKey) : item.defaultLabel || item.label}
									</button>
								</li>
							);
						})}

						{/* If not logged in, show Login */}
						{!currentUser && (
							<li>
								<button
									onClick={() => navigate('/login')}
									className='px-3 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium border text-blue-600 hover:bg-blue-100'
									type='button'
								>
									{t ? t('signIn') : 'Login'}
								</button>
							</li>
						)}

						{/* Language selector */}
						<li className='relative'>
							<button
								onClick={() => setLangMenuOpen((s) => !s)}
								className='px-3 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium border flex items-center space-x-2'
								aria-haspopup='true'
								aria-expanded={langMenuOpen}
								aria-label='Select language'
							>
								<span>{langLabels[locale] || locale.toUpperCase()}</span>
								<svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
									<path
										fillRule='evenodd'
										d='M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.12 1l-4.25 4.65a.75.75 0 01-1.12 0L5.21 8.27a.75.75 0 01.02-1.06z'
										clipRule='evenodd'
									/>
								</svg>
							</button>

							{langMenuOpen && (
								<ul className='absolute right-0 mt-2 w-28 bg-white border border-gray-200 rounded-md shadow-lg z-50'>
									{languages.map((l) => (
										<li key={l}>
											<button
												onClick={() => {
													setLocale(l);
													setLangMenuOpen(false);
												}}
												className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${l === locale ? 'font-semibold' : ''}`}
											>
												{langLabels[l]}
											</button>
										</li>
									))}
								</ul>
							)}
						</li>
					</ul>
					{/* Mobile dropdown menu */}
					{menuOpen && (
						<ul className='absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 flex flex-col sm:hidden animate-fade-in'>
							{menuItems.map((item) => {
								const key = item.labelKey || item.defaultLabel || item.label;
								return (
									<li key={key}>
										<button
											onClick={() => {
												setMenuOpen(false);
												item.onClick();
											}}
											className={`w-full text-left px-4 py-2 rounded-none border-b last:border-b-0 border-gray-100 text-sm ${item.className}`}
										>
											{item.labelKey ? t(item.labelKey) : item.defaultLabel || item.label}
										</button>
									</li>
								);
							})}

							{/* Login for mobile when not logged in */}
							{!currentUser && (
								<li>
									<button
										onClick={() => {
											setMenuOpen(false);
											navigate('/login');
										}}
										className='w-full text-left px-4 py-2 border-b last:border-b-0 border-gray-100 text-sm text-blue-600'
										type='button'
									>
										{t ? t('signIn') : 'Login'}
									</button>
								</li>
							)}

							{/* Language choices for mobile menu */}
							<li className='px-2 py-1'>
								<div className='text-xs text-gray-500 px-2 pb-1'>Language</div>
								{languages.map((l) => (
									<button
										key={l}
										onClick={() => {
											setMenuOpen(false);
											setLocale(l);
										}}
										className={`w-full text-left px-4 py-2 border-b last:border-b-0 border-gray-100 text-sm ${l === locale ? 'font-semibold' : ''}`}
										type='button'
									>
										{langLabels[l]}
									</button>
								))}
							</li>
						</ul>
					)}
				</nav>
			</div>
		</header>
	);
};

export default TopHeader;
